import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {TechnicalIssueData} from './types';
import {
    technicalIssueDefaultValues,
    technicalIssuePrioritySubTitle,
    technicalIssueTypePlaceholder,
} from './constants';
import {getRequiredAdornment} from 'utils/functions/getRequiredAdornment';
import {TechnicalSupportModalFiles} from './TechnicalSupportModalFiles';
import {useEnumDictionary} from 'utils/hooks/useEnumDictionary';
import {TechnicalIssuePriority} from '@services/TechnicalSupport/types';
import {useMutation} from '@tanstack/react-query';
import {TechnicalSupportService} from '@services/TechnicalSupport/TechnicalSupportService';
import {useDispatch} from 'react-redux';
import {setNotification} from 'slices/notifications/notificationsSlice';

type TechnicalSupportModalProps = {onClose: () => void};

export const TechnicalSupportModal = ({onClose}: TechnicalSupportModalProps) => {
    const {t} = useTranslation(['administration', 'common']);
    const dispatch = useDispatch();

    const {dictionaryFull: technicalIssueTypeOptions} = useEnumDictionary({
        code: 'SupportTicketType',
    });
    const {dictionaryFull: technicalIssuePriorityOptions} = useEnumDictionary({
        code: 'SupportTicketPriority',
    });

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: {isValid},
    } = useForm<TechnicalIssueData>({defaultValues: technicalIssueDefaultValues});

    const watchType = watch('type');

    const setFiles = (newFiles: string[]) => {
        setValue('files', newFiles);
    };

    const mutateCreateTechnicalIssue = useMutation({
        mutationKey: ['createTechnicalIssue'],
        mutationFn: (values: TechnicalIssueData) =>
            TechnicalSupportService.createTechnicalIssue(values),
        onSuccess: () => {
            onClose();
            dispatch(
                setNotification({
                    text: t('technicalSupport.success'),
                    variant: 'success',
                }),
            );
        },
    });

    const onSubmit = (values: TechnicalIssueData) => {
        mutateCreateTechnicalIssue.mutate(values);
    };

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth='md'
            fullWidth
        >
            <DialogTitle>{t('technicalSupport.addIssue')}</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent
                    sx={{
                        paddingTop: '0',
                        '& .MuiTypography-root:not(.MuiFormControlLabel-label)': {
                            color: 'var(--mui-palette-text-secondary)',
                        },
                    }}
                >
                    <Stack
                        direction='row'
                        gap={2}
                        flexGrow={1}
                    >
                        <Stack gap={2}>
                            <Stack>
                                <Typography variant='subtitle1'>
                                    {t('technicalSupport.type')}
                                </Typography>
                                <Controller
                                    control={control}
                                    name='type'
                                    render={({field}) => (
                                        <RadioGroup {...field}>
                                            {technicalIssueTypeOptions.map(
                                                ({code, displayName}) => (
                                                    <FormControlLabel
                                                        value={code}
                                                        key={code}
                                                        control={<Radio />}
                                                        label={displayName}
                                                    />
                                                ),
                                            )}
                                        </RadioGroup>
                                    )}
                                />
                            </Stack>
                            <Stack>
                                <Typography variant='subtitle1'>
                                    {t('technicalSupport.priority')}
                                </Typography>
                                <Controller
                                    control={control}
                                    name='priority'
                                    render={({field}) => (
                                        <RadioGroup {...field}>
                                            {technicalIssuePriorityOptions.map(
                                                ({code, displayName}) => (
                                                    <FormControlLabel
                                                        value={code}
                                                        key={code}
                                                        control={<Radio />}
                                                        label={
                                                            <Stack>
                                                                <Typography
                                                                    variant='body1'
                                                                    style={{
                                                                        color: 'var(--mui-palette-text-primary)',
                                                                    }}
                                                                >
                                                                    {displayName}
                                                                </Typography>
                                                                <Typography
                                                                    variant='body2'
                                                                    color='text.disabled'
                                                                >
                                                                    {code &&
                                                                        t(
                                                                            technicalIssuePrioritySubTitle[
                                                                                code as TechnicalIssuePriority
                                                                            ],
                                                                        )}
                                                                </Typography>
                                                            </Stack>
                                                        }
                                                    />
                                                ),
                                            )}
                                        </RadioGroup>
                                    )}
                                />
                            </Stack>
                        </Stack>
                        <Divider
                            orientation='vertical'
                            flexItem
                        />
                        <Stack
                            flexGrow={1}
                            gap={1}
                        >
                            <Stack>
                                <Typography variant='subtitle1'>
                                    {t('technicalSupport.section')}
                                </Typography>
                                <Controller
                                    control={control}
                                    name='section'
                                    rules={{required: true}}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            size='small'
                                            slotProps={{
                                                htmlInput: {
                                                    maxLength: 250,
                                                },
                                                input: {
                                                    ...getRequiredAdornment({
                                                        value: field.value,
                                                        required: true,
                                                    }),
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Stack>
                            <Stack>
                                <Typography variant='subtitle1'>{t('description')}</Typography>
                                <Controller
                                    control={control}
                                    name='description'
                                    rules={{required: true}}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            size='small'
                                            multiline
                                            rows={5}
                                            placeholder={t(
                                                technicalIssueTypePlaceholder[watchType],
                                            )}
                                            slotProps={{
                                                htmlInput: {
                                                    maxLength: 1000,
                                                },
                                                input: {
                                                    ...getRequiredAdornment({
                                                        value: field.value,
                                                        required: true,
                                                        styles: {alignSelf: 'baseline'},
                                                    }),
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Stack>
                            <TechnicalSupportModalFiles setFiles={setFiles} />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t('cancel')}</Button>
                    <Button
                        type='submit'
                        disabled={!isValid}
                        variant='contained'
                        color='primary'
                    >
                        {t('send')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
